'use client';

import { FormEvent, useEffect, useState } from 'react';

const REMEMBERED_EMAIL_KEY = 'rememberedEmail';

type Props = {
    initialError?: string | null;
};

export default function LoginForm({ initialError = null }: Props) {
    const [showPassword, setShowPassword] = useState(false);
    const [remember, setRemember] = useState(false);

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');

    // error que viene de la query ?error=
    const [error, setError] = useState<string | null>(initialError);

    useEffect(() => {
        const savedEmail = localStorage.getItem(REMEMBERED_EMAIL_KEY);
        if (savedEmail) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setEmail(savedEmail);
            setRemember(true);
        }
    }, []);

    const handleSubmit = (_e: FormEvent<HTMLFormElement>) => {
        setError(null);

        // Guardar o limpiar email en localStorage
        if (remember) {
            localStorage.setItem(REMEMBERED_EMAIL_KEY, email);
        } else {
            localStorage.removeItem(REMEMBERED_EMAIL_KEY);
        }
    };

    return (
        <form
            method="POST"
            action="/api/login"
            onSubmit={handleSubmit}
            className="mt-8 space-y-6 text-left"
        >
            <div className="h-auto rounded-2xl shadow-lg ring-1 ring-violet-900/10 bg-white/70 backdrop-blur px-6 py-8 sm:px-8 sm:py-8 pb-10 space-y-6 font-sans">
                {/* Email */}
                <div>
                    <label htmlFor="email" className="sr-only">
                        Email
                    </label>
                    <input
                        id="email"
                        name="email"
                        type="text"
                        autoComplete="username"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="block w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-violet-600 focus:ring-2 focus:ring-violet-600/30 text-gray-900 placeholder-gray-400 transition"
                        placeholder="Correo electrónico"
                    />
                </div>

                {/* Password + ojito */}
                <div className="relative">
                    <label htmlFor="password" className="sr-only">
                        Contraseña
                    </label>
                    <input
                        id="password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="current-password"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="block w-full px-4 py-3 pr-11 rounded-xl border border-gray-200 focus:border-violet-600 focus:ring-2 focus:ring-violet-600/30 text-gray-900 placeholder-gray-400 transition"
                        placeholder="Contraseña"
                    />
                    <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                        className="absolute inset-y-0 right-2.5 my-auto h-9 w-9 grid place-items-center rounded-lg hover:bg-gray-100 text-gray-500"
                        title={showPassword ? 'Ocultar' : 'Mostrar'}
                    >
                        {/* SVG Ojito */}
                        <svg
                            className="h-5 w-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            {showPassword ? (
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                                />
                            ) : (
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                />
                            )}
                            {/* 2da parte del SVG para el show/hide (simplificado para el ejemplo) */}
                            {!showPassword && (
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                />
                            )}
                        </svg>
                    </button>
                </div>

                {/* Error (mismo camino visual de antes) */}
                {error && (
                    <p className="text-sm font-medium text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2">
                        {error}
                    </p>
                )}

                {/* Recuérdame */}
                <div className="flex items-center justify-between">
                    <label className="flex items-center gap-2 text-sm text-gray-800">
                        <input
                            type="checkbox"
                            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                            checked={remember}
                            name="remember"
                            onChange={(e) => setRemember(e.target.checked)}
                        />
                        Recuérdame
                    </label>

                    <a
                        href="/olvide-pass"
                        className="text-sm font-semibold text-primary hover:text-primary/80"
                    >
                        Olvidé mi contraseña
                    </a>
                </div>

                <div className="mt-8 pb-2">
                    <button
                        type="submit"
                        className="group relative w-full flex justify-center py-3 px-4 text-[15px] font-semibold rounded-2xl text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition"
                    >
                        Ingresar
                    </button>
                </div>
            </div>
        </form>
    );
}
